import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { HrmsAccessRecord, hrmsAccessService } from "../services/hrmsAccessService";

interface HrmsAccessState {
  loading: boolean;
  error: string | null;
  access: HrmsAccessRecord | null;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
  canAdmin: boolean;
  canEmployeeConfigRead: boolean;
  canEmployeeConfigWrite: boolean;
  canSalarySlipRead: boolean;
  canSalarySlipWrite: boolean;
  isReportingManager: boolean;
}

const INITIAL_STATE: HrmsAccessState = {
  loading: true,
  error: null,
  access: null,
  canRead: false,
  canWrite: false,
  canDelete: false,
  canAdmin: false,
  canEmployeeConfigRead: false,
  canEmployeeConfigWrite: false,
  canSalarySlipRead: false,
  canSalarySlipWrite: false,
  isReportingManager: false,
};

const HrmsAccessContext = createContext<HrmsAccessState>(INITIAL_STATE);

export const useHrmsAccess = () => useContext(HrmsAccessContext);

export const HrmsAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState<HrmsAccessState>(INITIAL_STATE);

  useEffect(() => {
    let active = true;

    const loadAccess = async () => {
      if (!user?.id) {
        setState({ ...INITIAL_STATE, loading: false });
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const access = await hrmsAccessService.getUserAccess();
        if (!active) return;

        const canRead = Boolean(access?.read_access || access?.write_access || access?.delete_access || user.isadmin);
        const canWrite = Boolean(access?.write_access || access?.delete_access || user.isadmin);
        const canDelete = Boolean(access?.delete_access || user.isadmin);
        const canAdmin = Boolean(access?.admin_access || user.isadmin);
        const canEmployeeConfigRead = Boolean(
          access?.employee_config_read_access || access?.employee_config_write_access || user.isadmin
        );
        const canEmployeeConfigWrite = Boolean(access?.employee_config_write_access || user.isadmin);
        const canSalarySlipRead = Boolean(
          (access?.salary_slip_read_access && access?.salary_slip_write_access) || user.isadmin
        );
        const canSalarySlipWrite = Boolean(access?.salary_slip_write_access || user.isadmin);
        const isReportingManager = Boolean(access?.is_reporting_manager);

        setState({
          loading: false,
          error: null,
          access,
          canRead,
          canWrite,
          canDelete,
          canAdmin,
          canEmployeeConfigRead,
          canEmployeeConfigWrite,
          canSalarySlipRead,
          canSalarySlipWrite,
          isReportingManager,
        });
      } catch (error: any) {
        if (!active) return;
        setState({
          loading: false,
          error: error?.message || "Failed to load HRMS access",
          access: null,
          canRead: Boolean(user?.isadmin),
          canWrite: Boolean(user?.isadmin),
          canDelete: Boolean(user?.isadmin),
          canAdmin: Boolean(user?.isadmin),
          canEmployeeConfigRead: Boolean(user?.isadmin),
          canEmployeeConfigWrite: Boolean(user?.isadmin),
          canSalarySlipRead: Boolean(user?.isadmin),
          canSalarySlipWrite: Boolean(user?.isadmin),
          isReportingManager: false,
        });
      }
    };

    loadAccess();
    return () => {
      active = false;
    };
  }, [user?.id, user?.isadmin]);

  const value = useMemo(() => state, [state]);

  return <HrmsAccessContext.Provider value={value}>{children}</HrmsAccessContext.Provider>;
};
